import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SearchWidget } from "@/components/SearchWidget";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PromptPayDialog } from "@/components/PromptPayDialog";
import { Bus, ArrowRight, Wifi, Zap, Wind, Coffee, Armchair, Clock, Loader2 } from "lucide-react";

const amenityIcon = (a) => {
  const map = { "Wi-Fi": Wifi, "Power": Zap, "AC": Wind, "Snacks": Coffee, "Extra legroom": Armchair, "WC": Bus };
  const Icon = map[a] || Bus;
  return <Icon size={13} />;
};

const BusTickets = () => {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const date = params.get("date") || "";
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);
  const [payBooking, setPayBooking] = useState(null);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (from) q.from_city = from; if (to) q.to_city = to; if (date) q.date = date;
    api.get("/bus/trips", { params: q }).then((r) => setTrips(r.data)).catch(() => setTrips([])).finally(() => setLoading(false));
  }, [from, to, date]);

  const book = async (trip) => {
    if (!user) { navigate("/auth", { state: { from: "/bus" } }); return; }
    setBookingId(trip.trip_id);
    try { const res = await api.post("/bus/bookings", { trip_id: trip.trip_id, seats: 1 }); setPayBooking(res.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); } finally { setBookingId(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center gap-2 mb-4"><div className="w-11 h-11 rounded-xl grid place-items-center glass-solid glow-gold"><Bus className="text-[#F7C948]" size={22} /></div><div><h1 className="font-display text-2xl font-semibold">Bus tickets</h1><p className="text-sm text-muted-cosmic">Comfy long-distance coaches across the galaxy.</p></div></div>

      <SearchWidget variant="bus" initial={{ from, to, date }} className="mb-6" />

      <p className="text-sm text-muted-cosmic mb-4">{loading ? "Searching coaches…" : `${trips.length} trip${trips.length === 1 ? "" : "s"} found`}</p>

      <div className="space-y-3">
        {loading ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full bg-white/5 rounded-[var(--radius)]" />) :
          trips.length === 0 ? (
            <Card className="glass p-10 text-center"><Bus className="mx-auto text-[#7C3AED] mb-3" size={36} /><h3 className="font-display text-xl">No coaches on this route yet</h3><p className="text-muted-cosmic mt-2 text-sm">Try another city pair or date.</p></Card>
          ) : trips.map((t) => (
            <Card key={t.trip_id} data-testid="bus-result-card" className="glass p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><Badge className="bg-[rgba(124,58,237,0.16)] text-[#C7D2FE] border border-[rgba(124,58,237,0.25)]">{t.operator}</Badge><span className="text-xs text-muted-cosmic font-mono-cosmic">{t.date}</span></div>
                  <div className="mt-2 flex items-center gap-3 font-display text-xl"><span>{t.time}</span><span className="text-white">{t.from_city}</span><ArrowRight size={16} className="text-[#7C3AED]" /><span className="text-white">{t.to_city}</span><span>{t.arrival_time}</span></div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-cosmic"><Clock size={12} /> {t.duration}</div>
                  <div className="mt-3 flex flex-wrap gap-2">{(t.amenities || []).map((a) => <Badge key={a} className="bg-[rgba(34,211,238,0.1)] text-[#A5F3FC] border border-[rgba(34,211,238,0.22)] gap-1">{amenityIcon(a)} {a}</Badge>)}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-semibold text-white">{t.price} <span className="text-sm text-muted-cosmic">฿</span></div>
                  <div className="text-xs text-muted-cosmic mb-2">{t.seats_available} seats left</div>
                  <Button data-testid="bus-book-button" onClick={() => book(t)} disabled={bookingId === t.trip_id} className="bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-1">{bookingId === t.trip_id ? <Loader2 className="animate-spin" size={15} /> : "Book"}</Button>
                </div>
              </div>
            </Card>
          ))}
      </div>
      <p className="text-xs text-muted-cosmic text-center mt-6">Pay securely with PromptPay after booking.</p>
      <PromptPayDialog booking={payBooking} type="bus" open={!!payBooking} onOpenChange={(o) => { if (!o) setPayBooking(null); }} onConfirmed={() => navigate("/dashboard")} />
    </div>
  );
};

export default BusTickets;
